import { useCallback, useEffect, useRef, useState } from 'react'

const cache: Record<string, any> = {}

/**
 * Fetches data with an in-memory cache.
 * On revisit, returns cached data instantly while refreshing in background.
 * @param cacheKey - unique key (e.g. "entities:acme"). Pass null to skip fetching.
 * @param fetcher - async function that returns data
 */
export function useCachedFetch<T>(cacheKey: string | null, fetcher: () => Promise<T>) {
    const cached = cacheKey ? cache[cacheKey] as T | undefined : undefined
    const [data, setData] = useState<T | undefined>(cached)
    const [isLoading, setIsLoading] = useState(!cached && !!cacheKey)
    const [hasError, setHasError] = useState(false)
    const fetchIdRef = useRef(0)
    const fetcherRef = useRef(fetcher)
    fetcherRef.current = fetcher

    useEffect(() => {
        if (!cacheKey) {
            setData(undefined)
            setIsLoading(false)
            return
        }

        // Show cached data immediately
        if (cache[cacheKey]) {
            setData(cache[cacheKey])
            setHasError(false)
        }

        const id = ++fetchIdRef.current
        if (!cache[cacheKey]) setIsLoading(true)
        setHasError(false)

        fetcherRef.current().then(result => {
            if (id !== fetchIdRef.current) return
            cache[cacheKey] = result
            setData(result)
            setIsLoading(false)
        }).catch(() => {
            if (id !== fetchIdRef.current) return
            if (!cache[cacheKey]) setHasError(true)
            setIsLoading(false)
        })
    }, [cacheKey])

    const refetch = useCallback(() => {
        if (!cacheKey) return
        const id = ++fetchIdRef.current
        // Don't clear stale data or set isLoading — fetch silently in background
        // so the UI stays visible while fresh data arrives.
        setHasError(false)
        fetcherRef.current().then(result => {
            if (id !== fetchIdRef.current) return
            cache[cacheKey] = result
            setData(result)
        }).catch(() => {
            if (id !== fetchIdRef.current) return
            setHasError(true)
        })
    }, [cacheKey])

    return { data, isLoading, hasError, refetch }
}
